import { Link, useRouterState } from '@tanstack/react-router';
import { Activity, LogOut, Server, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useAuthStore } from '../../../stores/auth-store.js';
import { cn } from '../../../lib/utils.js';
import { logout } from '../../../features/auth/api.js';

interface NavItem {
  to: string;
  icon: typeof Server;
  label: string;
}

const NAV: NavItem[] = [
  { to: '/', icon: Server, label: 'Servidores' },
  { to: '/super-users', icon: Users, label: 'Super Users' },
  { to: '/auditoria', icon: Activity, label: 'Auditoria' },
];

/**
 * Casca do super — replica o layout do super.old (web-v2): Topbar fixo no topo
 * + rail de 64px à esquerda que expande pra 220px no hover. Hexes batem com o
 * MetricsFarma app (#0a4ee3 brand, #f8fafc app bg, #eff4ff item ativo).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const [collapsed, setCollapsed] = useState(true);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch {
      /* mesmo se falhar, limpa local */
    }
    clear();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Topbar */}
      <header className="fixed top-0 left-0 right-0 z-30 flex h-14 items-center justify-between border-b border-[#e2e8f0] bg-white pr-6">
        <div className="flex items-center">
          <div className="flex w-[64px] shrink-0 items-center justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0a4ee3]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 17l4-8 4 4 4-6 4 5"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M3 21h18" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-bold tracking-tight text-[#0f172a]">MetricsFarma</span>
            <span className="text-[10px] text-[#94a3b8]">Super Admin</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden flex-col items-end leading-tight sm:flex">
            <span className="text-[13px] font-medium text-[#0f172a]">{user?.name}</span>
            <span className="text-[11px] text-[#94a3b8]">{user?.email}</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-[13px] font-medium text-[#64748b] transition-colors hover:bg-[#f8fafc] hover:text-[#ef4444]"
            title="Sair"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>

      {/* Rail (64px → 220px no hover) */}
      <aside
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
        className={cn(
          'fixed left-0 top-14 bottom-0 z-20 flex flex-col overflow-hidden border-r border-[#e2e8f0] bg-white transition-all duration-200 ease-in-out',
          collapsed ? 'w-[64px]' : 'w-[220px]',
        )}
      >
        <nav className="mt-2 flex flex-col gap-1 p-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active =
              item.to === '/' ? currentPath === '/' : currentPath.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150',
                  active
                    ? 'bg-[#eff4ff] text-[#0a4ee3]'
                    : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]',
                )}
              >
                <span className="shrink-0">
                  <Icon size={20} strokeWidth={2} />
                </span>
                <span
                  className={cn(
                    'whitespace-nowrap transition-opacity duration-200',
                    collapsed ? 'opacity-0' : 'opacity-100',
                  )}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Conteúdo */}
      <div className="flex pt-14">
        <div className="w-[64px] shrink-0" />
        <main className="min-w-0 flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}
